"use client";
import { useTranslations } from "next-intl";
import { Room, RoomEvent } from "livekit-client";
import { useState, useEffect, useCallback } from "react";
import { useAppSelector, useAppDispatch } from "@client/store/store.hooks";
import { updateMeetingDetailsRedux } from "@client/store/slices/meeting.slice";
import { startRecording, stopRecording, getActiveRecordingStatus } from "@client/actions/recording/recording.actions";

interface UseRecordingOptions {
  room: Room;
}

interface UseRecordingReturn {
  isRecording: boolean;
  isLoading: boolean;
  error: string | null;
  egressId: string | null;
  recordingStartedAt: string | null;
  handleStartRecording: () => Promise<void>;
  handleStopRecording: () => Promise<void>;
}

/**
 * Syncs recording state with Redux for global access.
 * Restores recording state on page refresh/reconnect.
 *
 * For all participants (host and non-host), listens to LiveKit's
 * RoomEvent.RecordingStatusChanged to detect when recording starts/stops.
 * This ensures every user in the room sees the recording indicator.
 */
export const useRecording = ({ room }: UseRecordingOptions): UseRecordingReturn => {
  const t = useTranslations();
  const dispatch = useAppDispatch();

  const [isRecording, setIsRecording] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [egressId, setEgressId] = useState<string | null>(null);
  const [hasCheckedActiveRecording, setHasCheckedActiveRecording] = useState(false);

  const connectionDetails = useAppSelector((state) => state.connection.connectionDetails);
  const [recordingStartedAt, setRecordingStartedAt] = useState<string | null>(null);

  const syncActiveRecordingStatus = useCallback(
    async (includeStartedAt = true) => {
      if (!room?.name || !connectionDetails) return false;

      try {
        const response = await getActiveRecordingStatus(room.name);

        if (response.status && response.hasActiveRecording && response.activeRecording) {
          setIsRecording(true);
          setEgressId(response.activeRecording.egressId);
          if (includeStartedAt) {
            setRecordingStartedAt(response.activeRecording.startedAt);
          }
          return true;
        }

        setIsRecording(false);
        setEgressId(null);
        setRecordingStartedAt(null);
        return false;
      } catch {
        return false;
      }
    },
    [connectionDetails, room]
  );

  /**
   * Listen to LiveKit's RecordingStatusChanged event.
   * This is broadcast by the LiveKit server to ALL participants when an egress
   * starts or stops, ensuring non-host users also see the recording indicator.
   */
  useEffect(() => {
    if (!room) return;

    // Sync initial state from room (covers joining a room that's already recording)
    if (room.isRecording && !isRecording) {
      setIsRecording(true);
    }

    const onRecordingStatusChanged = (recording: boolean) => {
      setIsRecording(recording);
      // When recording stops via server signal, clear the egressId
      if (!recording) {
        setEgressId(null);
      }
    };

    room.on(RoomEvent.RecordingStatusChanged, onRecordingStatusChanged);
    return () => {
      room.off(RoomEvent.RecordingStatusChanged, onRecordingStatusChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);

  /**
   * Check for active recording on mount/reconnect (host only).
   * Restores egressId so the host can stop the recording.
   * Non-host users get recording status via RoomEvent.RecordingStatusChanged above.
   */
  useEffect(() => {
    const checkActiveRecording = async () => {
      if (hasCheckedActiveRecording || !room?.name) {
        return;
      }

      if (!connectionDetails) {
        return;
      }

      await syncActiveRecordingStatus(true);
      setHasCheckedActiveRecording(true);
    };

    checkActiveRecording();
  }, [room?.name, connectionDetails, hasCheckedActiveRecording, syncActiveRecordingStatus]);

  /**
   * While recording is active, periodically re-check backend status.
   * This ensures guests see stop/start changes even if the realtime event is missed.
   */
  useEffect(() => {
    if (!isRecording || isLoading || !room?.name) {
      return;
    }

    const interval = setInterval(() => {
      void syncActiveRecordingStatus(false);
    }, 5000);

    return () => clearInterval(interval);
  }, [isRecording, isLoading, room?.name, syncActiveRecordingStatus]);

  // Sync recording state to Redux whenever it changes
  useEffect(() => {
    dispatch(
      updateMeetingDetailsRedux({
        isRecording,
        isRecordingLoading: isLoading,
        recordingError: error
      })
    );
  }, [dispatch, isRecording, isLoading, error]);

  /**
   * Start Recording Handler
   */
  const handleStartRecording = useCallback(async () => {
    if (isLoading || isRecording) {
      return;
    }

    if (!connectionDetails?.hostId) {
      setError(t("errors.hostIdNotFound"));
      return;
    }

    if (!room?.name) {
      setError(t("errors.roomInfoUnavailable"));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await startRecording({
        hostId: Number(connectionDetails.hostId),
        roomName: room.name
      });

      if (response.status) {
        setIsRecording(true);
        setEgressId(response.egressId || null);
        setRecordingStartedAt(new Date().toISOString());
      } else {
        const errorMessage = response.message || t("errors.startRecordingFailed");

        if (
          errorMessage.toLowerCase().includes("already being recorded") ||
          errorMessage.toLowerCase().includes("already recording")
        ) {
          setIsRecording(true);
          try {
            const statusResponse = await getActiveRecordingStatus(room.name);
            if (statusResponse.status && statusResponse.hasActiveRecording && statusResponse.activeRecording) {
              setEgressId(statusResponse.activeRecording.egressId);
            }
          } catch {
            // Ignore
          }
        } else {
          setError(errorMessage);
        }
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t("errors.startRecordingGeneric");
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [connectionDetails, room, isLoading, isRecording, t]);

  /**
   * Stop Recording Handler
   */
  const handleStopRecording = useCallback(async () => {
    if (isLoading || !isRecording) {
      return;
    }

    if (!connectionDetails?.hostId) {
      setError(t("errors.hostIdNotFound"));
      return;
    }

    if (!room?.name) {
      setError(t("errors.roomInfoUnavailable"));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await stopRecording({
        hostId: Number(connectionDetails.hostId),
        roomName: room.name,
        egressId: egressId || undefined
      });

      if (response.status) {
        setIsRecording(false);
        setEgressId(null);
        setRecordingStartedAt(null);
      } else {
        const errorMessage = response.message || t("errors.stopRecordingFailed");

        if (
          errorMessage.toLowerCase().includes("cannot be stopped") ||
          errorMessage.toLowerCase().includes("has failed")
        ) {
          setIsRecording(false);
          setEgressId(null);
          setRecordingStartedAt(null);
        }

        setError(errorMessage);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t("errors.stopRecordingGeneric");
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [connectionDetails, room, isLoading, isRecording, egressId, t]);

  /**
   * Clear error after 5 seconds
   */
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return {
    isRecording,
    isLoading,
    error,
    egressId,
    recordingStartedAt,
    handleStartRecording,
    handleStopRecording
  };
};
